'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { siteConfig } from '@/config/site';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Prevent hydration mismatch
  if (!isMounted) {
    return (
      <header className="fixed w-full z-50 bg-black/90 backdrop-blur-md py-2">
        <div className="container mx-auto px-4 h-16 flex items-center">
          <div className="w-48 h-10 bg-gray-700 rounded animate-pulse"></div>
        </div>
      </header>
    );
  }

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="logo">
          <Link href="/" className="flex items-center">
            <Image 
              src="/images/logo.png" 
              alt={`${siteConfig.name} Logo`} 
              width={180} 
              height={50}
              className="h-8 w-auto md:h-10"
              priority
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <ul className="flex items-center space-x-8">
            {siteConfig.mainNav.map((item) => (
              <li key={item.href}>
                <Link 
                  href={item.href}
                  className="text-white hover:text-blue-400 transition-colors font-medium text-sm uppercase tracking-wider"
                >
                  {item.title}
                </Link>
              </li>
            ))}
            <li>
              <Link 
                href="/get-started" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full transition-colors text-sm font-medium"
              >
                Get Started
              </Link>
            </li>
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white text-2xl z-50"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>

        {/* Mobile Navigation */}
        <div 
          className={`
            fixed inset-0 bg-black/95 backdrop-blur-sm z-40 transition-opacity duration-300 ease-in-out
            ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            md:hidden
          `}
          onClick={toggleMenu}
        >
          <div 
            className={`
              fixed top-0 right-0 h-full w-4/5 max-w-sm bg-gray-900 shadow-2xl z-50
              transform transition-transform duration-300 ease-in-out
              ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full overflow-y-auto">
              <div className="p-6 border-b border-gray-800">
                <Link href="/" className="inline-block" onClick={toggleMenu}>
                  <Image 
                    src="/images/logo.png" 
                    alt={`${siteConfig.name} Logo`}
                    width={150}
                    height={40}
                    className="h-8 w-auto"
                  />
                </Link>
              </div>
              
              <nav className="p-6">
                <ul className="space-y-6">
                  {siteConfig.mainNav.map((item) => (
                    <li key={item.href}>
                      <Link 
                        href={item.href}
                        className="block text-white hover:text-blue-400 transition-colors py-2 text-lg"
                        onClick={toggleMenu}
                      >
                        {item.title}
                      </Link>
                    </li>
                  ))}
                  <li className="pt-4 mt-4 border-t border-gray-800">
                    <Link 
                      href="/get-started" 
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full transition-colors w-full text-center"
                      onClick={toggleMenu}
                    >
                      Get Started
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
